import type { ReactNode, RefObject } from "react";

import Spinner from "./Spinner";

type TablePanelProps = {
  title?: string;
  subtitle?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
};

export function TablePanel({
  title,
  subtitle,
  children,
  footer,
  className,
}: TablePanelProps) {
  return (
    <section
      className={[
        "overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm",
        className ?? "",
      ]
        .join(" ")
        .trim()}
    >
      {title || subtitle ? (
        <div className="border-b border-slate-200 px-4 py-3">
          {title ? (
            <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          ) : null}
          {subtitle ? (
            <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
          ) : null}
        </div>
      ) : null}
      <div className="overflow-x-auto">{children}</div>
      {footer ? (
        <footer className="border-t border-slate-100 bg-slate-50 px-4 py-3 text-center text-xs text-slate-500">
          {footer}
        </footer>
      ) : null}
    </section>
  );
}

export function TableHeadCell({
  children,
  align = "left",
  hint,
}: {
  children: ReactNode;
  align?: "left" | "right";
  hint?: string;
}) {
  return (
    <th
      scope="col"
      title={hint}
      className={`px-3 py-2 font-medium ${
        align === "right" ? "text-right" : "text-left"
      }`}
    >
      {children}
    </th>
  );
}

export function TableStatusRow({
  colSpan,
  children,
  tone = "muted",
}: {
  colSpan: number;
  children: ReactNode;
  tone?: "muted" | "danger";
}) {
  const toneClass = tone === "danger" ? "text-rose-700" : "text-slate-500";
  return (
    <tr>
      <td colSpan={colSpan} className={`px-3 py-8 text-center ${toneClass}`}>
        {children}
      </td>
    </tr>
  );
}

export function TableLoadingRow({
  colSpan,
  label,
}: {
  colSpan: number;
  label: string;
}) {
  return (
    <TableStatusRow colSpan={colSpan}>
      <Spinner label={label} />
    </TableStatusRow>
  );
}

export function TableErrorRow({
  colSpan,
  children,
}: {
  colSpan: number;
  children: ReactNode;
}) {
  return (
    <TableStatusRow colSpan={colSpan} tone="danger">
      {children}
    </TableStatusRow>
  );
}

export function TableEmptyRow({
  colSpan,
  children,
}: {
  colSpan: number;
  children: ReactNode;
}) {
  return <TableStatusRow colSpan={colSpan}>{children}</TableStatusRow>;
}

export function TableLoadMoreFooter({
  loadMoreRef,
  loadedCount,
  totalCount,
  itemLabel,
  moreItemLabel,
  hasMore,
  isLoading,
  buttonLabel,
  loadingLabel,
  onLoadMore,
}: {
  loadMoreRef?: RefObject<HTMLDivElement | null>;
  loadedCount: number;
  totalCount?: number | null;
  itemLabel: string;
  moreItemLabel?: string;
  hasMore: boolean;
  isLoading: boolean;
  buttonLabel: string;
  loadingLabel: string;
  onLoadMore: () => void;
}) {
  return (
    <div
      ref={loadMoreRef}
      className="relative z-10 inline-flex flex-col items-center gap-2"
    >
      <span>
        {loadedCount.toLocaleString("de-AT")}
        {totalCount != null ? ` von ${totalCount.toLocaleString("de-AT")}` : ""}{" "}
        {itemLabel} angezeigt.
      </span>
      {hasMore ? (
        <>
          <button
            type="button"
            onClick={onLoadMore}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:cursor-default disabled:opacity-60"
          >
            {isLoading ? (
              <>
                <Spinner /> {loadingLabel}
              </>
            ) : (
              buttonLabel
            )}
          </button>
          <span>
            Weitere {moreItemLabel ?? itemLabel} werden beim Scrollen
            nachgeladen.
          </span>
        </>
      ) : null}
    </div>
  );
}

export function TableLoadMoreRow({
  colSpan,
  ...footerProps
}: Parameters<typeof TableLoadMoreFooter>[0] & { colSpan: number }) {
  return (
    <TableStatusRow colSpan={colSpan}>
      <TableLoadMoreFooter {...footerProps} />
    </TableStatusRow>
  );
}

export function FilterToolbar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={["mb-4 flex flex-wrap items-center gap-3", className ?? ""]
        .join(" ")
        .trim()}
    >
      {children}
    </section>
  );
}
